/**
 * Llama spit (vanilla LlamaSpit): a real projectile, simulated here rather than
 * in the arrow list so it can hit the player and other mobs alike. Gravity 0.06,
 * drag 0.99, 1 damage on contact, gone on the first block or after 100 ticks.
 * `spits` is the one shared sim: llamas `fire` into it, the TraderSystem ticks
 * it once per game tick and the SpitRenderer draws `views()`.
 */

import { collisionOf, IS_WATERY } from '../../blocks';
import type { World } from '../../world/chunk';
import { segmentAabb } from '../host';
import type { Mob } from '../mob';

export const SPIT_GRAVITY = 0.06;
export const SPIT_DAMAGE = 1;
export const SPIT_SPEED = 1.5;
const SPIT_LIFE = 100;
/** Hitbox inflation around a target (vanilla projectile margin 0.3 is for arrows; spit uses its own 0.25 box). */
const MARGIN = 0.25;

export interface SpitView { id: number; x: number; y: number; z: number; px: number; py: number; pz: number }

interface Spit extends SpitView { vx: number; vy: number; vz: number; life: number; owner: Mob | null; ownerId: number }

export interface SpitEnv {
  world: World;
  mobs: readonly Mob[];
  player: { x: number; y: number; z: number; width: number; height: number; alive: boolean; creative: boolean; spectator: boolean };
  /** Damage the player (the glue routes this through PlayerStats). */
  hurtPlayer(amount: number, x: number, y: number, z: number, attackerId: number): void;
  hurtMob(owner: Mob | null, target: Mob, amount: number): void;
  /** Mobs the spit passes through (the shooter's own kind). */
  ignores?(owner: Mob | null, target: Mob): boolean;
}

export class SpitSim {
  readonly list: Spit[] = [];
  private nextId = 1;
  private readonly cache: SpitView[] = [];

  /** Launch from (x, y, z) along (dx, dy, dz) at `speed`, spread by `inaccuracy` (vanilla 10). */
  fire(owner: Mob | null, x: number, y: number, z: number, dx: number, dy: number, dz: number,
    rnd: () => number, speed = SPIT_SPEED, inaccuracy = 10): void {
    const l = Math.hypot(dx, dy, dz) || 1;
    const g = (): number => (rnd() + rnd() + rnd() - 1.5) * 0.0075 * inaccuracy * 1.15;
    let nx = dx / l + g(), ny = dy / l + g(), nz = dz / l + g();
    const nl = Math.hypot(nx, ny, nz) || 1;
    nx /= nl; ny /= nl; nz /= nl;
    this.list.push({ id: this.nextId++, x, y, z, px: x, py: y, pz: z, vx: nx * speed, vy: ny * speed, vz: nz * speed, life: 0, owner, ownerId: owner?.id ?? 0 });
  }

  clear(): void { this.list.length = 0; }

  tick(env: SpitEnv): void {
    const w = env.world;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const s = this.list[i];
      s.px = s.x; s.py = s.y; s.pz = s.z;
      if (++s.life > SPIT_LIFE) { this.list.splice(i, 1); continue; }
      const wet = IS_WATERY[w.getBlock(Math.floor(s.x), Math.floor(s.y), Math.floor(s.z))] === 1;
      if (wet) { this.list.splice(i, 1); continue; }
      const dx = s.vx, dy = s.vy, dz = s.vz;
      // nearest thing along this tick's segment: a target box or a block
      let best = Infinity;
      let hitMob: Mob | null = null, hitPlayer = false;
      const p = env.player;
      if (p.alive && !p.creative && !p.spectator) {
        const hw = p.width / 2 + MARGIN;
        const t = segmentAabb(s.x, s.y, s.z, dx, dy, dz, [p.x - hw, p.y - MARGIN, p.z - hw, p.x + hw, p.y + p.height + MARGIN, p.z + hw]);
        if (t >= 0 && t < best) { best = t; hitPlayer = true; hitMob = null; }
      }
      for (const m of env.mobs) {
        if (!m.alive || m === s.owner || Math.abs(m.x - s.x) > 8 || Math.abs(m.z - s.z) > 8) continue;
        if (env.ignores?.(s.owner, m)) continue;
        const hw = m.width / 2 + MARGIN;
        const t = segmentAabb(s.x, s.y, s.z, dx, dy, dz, [m.x - hw, m.y - MARGIN, m.z - hw, m.x + hw, m.y + m.height + MARGIN, m.z + hw]);
        if (t >= 0 && t < best) { best = t; hitMob = m; hitPlayer = false; }
      }
      const tb = blockHit(w, s.x, s.y, s.z, dx, dy, dz);
      if (tb >= 0 && tb < best) { this.list.splice(i, 1); continue; }
      if (best <= 1) {
        if (hitPlayer) env.hurtPlayer(SPIT_DAMAGE, s.x, s.y, s.z, s.ownerId);
        else if (hitMob) env.hurtMob(s.owner, hitMob, SPIT_DAMAGE);
        this.list.splice(i, 1);
        continue;
      }
      s.x += dx; s.y += dy; s.z += dz;
      s.vx *= 0.99; s.vy = s.vy * 0.99 - SPIT_GRAVITY; s.vz *= 0.99;
    }
  }

  views(): readonly SpitView[] {
    this.cache.length = 0;
    for (const s of this.list) this.cache.push(s);
    return this.cache;
  }
}

/** Fraction (0..1) along the segment where it first enters a block with collision, or -1. */
function blockHit(w: World, x: number, y: number, z: number, dx: number, dy: number, dz: number): number {
  const n = Math.max(1, Math.ceil(Math.hypot(dx, dy, dz) / 0.25));
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const bx = Math.floor(x + dx * t), by = Math.floor(y + dy * t), bz = Math.floor(z + dz * t);
    if (by < 0) return t;
    if (collisionOf(w.getBlock(bx, by, bz)).length > 0) return t;
  }
  return -1;
}

export const spits = new SpitSim();
